import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  readCatalogFilters,
  toEvalRunScope,
  type PolicySelectionReader,
  type SessionScope,
} from "@/modules/training/public";
import { windowStack } from "@/app/windowStack";
import {
  readResultsPolicyIds,
  resultsPolicySearchParamKey,
  writeResultsPolicyIdsParam,
} from "./evaluationResultsUrl";
import {
  activeEvaluationResultsPolicyIds,
  openEvaluationResultsWindow,
} from "./evaluationResultsWindow";

/**
 * 활성 Evaluation Results 창의 정책 조합과 `?results_policy` 의 양방향 동기화를 소유한다.
 * 창은 app-level 이라 라우터에 접근할 수 없다. 그래서 이 페이지 훅이 유일한 writer 다. ReviewPage 의
 * `?review` 미러와 같은 방식이다. 미러는 항상 replace 로 써서 히스토리에 항목을 추가하지 않는다.
 *
 * - stack→URL: 활성 결과 창의 조합을 param 에 쓴다. 최소화·닫기·다른 kind 활성화는 param 을
 *   지운다. param 이 사라져도 창은 닫지 않는다.
 * - URL→stack: param 이 가리키는 조합의 창을 연다. team 스코프는 그룹 해석을 기다린다. 정책
 *   이름은 카탈로그 로드를 기다린다. 그동안 미러가 param 을 지우지 않도록 "처리됨" 표시로 막는다.
 *   `?policy=` 미러의 hydration 가드와 같은 계약이다.
 */
export function useResultsWindowUrlSync({
  scope,
  groupUuid,
  groupsResolving,
  policySource,
}: {
  scope: SessionScope;
  groupUuid: string | null;
  groupsResolving: boolean;
  policySource: PolicySelectionReader;
}): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSignature = readResultsPolicyIds(searchParams).join("+");
  // 미러가 stack 이벤트 때 최신 URL 을 읽도록 렌더마다 갱신한다.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  // "이 URL 값의 복원은 처리됨" 표시다. 미러는 아직 처리하지 않은 값(카탈로그 로드를 기다리는
  // 딥링크)을 지우지 않는다. 처리된 자기 기록은 창을 다시 열지 않는다.
  const settledSignatureRef = useRef<string | null>(null);

  // stack→URL. 처리된 상태에서 활성 창의 조합이 URL 과 다를 때만 쓴다. 자기 기록은 쓰기 전에
  // 처리됨으로 표시한다. 그래서 URL 처리 effect 가 같은 값을 다시 열지 않는다. 비교는 정규화 전
  // 원문으로 한다. 그래서 정렬되지 않거나 중복된 딥링크도 처리 뒤 정렬형으로 다시 써서 창 key 와
  // 1:1 로 맞춘다.
  const mirror = useCallback(() => {
    const url = searchParamsRef.current;
    if (settledSignatureRef.current !== readResultsPolicyIds(url).join("+")) {
      return;
    }
    const derivedIds = activeEvaluationResultsPolicyIds() ?? [];
    const derivedSignature = derivedIds.join("+");
    if (derivedSignature === url.getAll(resultsPolicySearchParamKey).join("+")) {
      return;
    }
    settledSignatureRef.current = derivedSignature;
    setSearchParams((prev) => writeResultsPolicyIdsParam(prev, derivedIds), { replace: true });
  }, [setSearchParams]);

  // URL→stack 을 처리하고, 끝에서 미러로 reconcile 한다. 열 수 없는 딥링크(그룹 없는 team, 창
  // cap 초과)와 같은 flush 안의 연속 stack 변경은 끝의 mirror 가 URL 을 실제 상태로 다시 맞춘다.
  useEffect(() => {
    const consumeUrl = () => {
      if (settledSignatureRef.current === urlSignature) {
        return;
      }
      const ids = readResultsPolicyIds(searchParams);
      if (ids.length === 0) {
        settledSignatureRef.current = urlSignature;
        return;
      }
      // 같은 조합이 이미 활성이면(scope 전환 remount 등) 다시 열지 않고 처리됨으로만 표시한다.
      const activeIds = activeEvaluationResultsPolicyIds();
      if (activeIds !== null && activeIds.join("+") === urlSignature) {
        settledSignatureRef.current = urlSignature;
        return;
      }
      if (scope === "team" && groupsResolving) {
        return;
      }
      // 그룹이 없는 team 스코프는 조회할 데이터가 없다. 처리됨으로 표시하고 mirror 가 param 을 지운다.
      const runScope = toEvalRunScope(scope, groupUuid);
      if (runScope === null) {
        settledSignatureRef.current = urlSignature;
        return;
      }
      const policies = policySource.resolvePolicyRefs(ids);
      if (policies === null) {
        return;
      }
      // open 이 stack 리스너(mirror)를 동기적으로 호출하므로 처리됨 표시를 먼저 한다.
      settledSignatureRef.current = urlSignature;
      openEvaluationResultsWindow({
        scope: runScope,
        policies,
        catalogFilters: readCatalogFilters(searchParams, scope),
      });
    };
    consumeUrl();
    mirror();
    if (settledSignatureRef.current === urlSignature) {
      return;
    }
    // 카탈로그 로드를 기다리는 처리만 소스 emit 에 재시도를 등록한다. 재렌더 없는 명령형 구독이다.
    // 정상 경로(딥링크 없음)에서는 구독하지 않는다. 그래서 표 조회가 페이지 재렌더를 만들지 않는다.
    return policySource.subscribe(() => {
      consumeUrl();
      mirror();
    });
  }, [urlSignature, scope, groupUuid, groupsResolving, policySource, searchParams, mirror]);

  useEffect(() => windowStack.subscribe(mirror), [mirror]);
}
